import { DOC_KINDS, DOC_STATUSES, type DocumentDetail, QC_STATUSES } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Printer, QrCode } from 'lucide-react';
import { useState } from 'react';
import { HistoryButton } from '../../components/History';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, tenge } from '../../lib/format';
import { useCan } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Textarea } from '../../ui/form';
import { KeyValue, Page, PageHeader, Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { DocumentDialog } from './forms';
import { DOC_TONE, documentQuery, parseNum, QC_TONE, qty } from './queries';

export function DocumentPage() {
  const { docId } = useParams({ strict: false }) as { docId: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Ссылка на договор — только тому, кто вправе его открыть; остальным номер текстом
  const canContracts = useCan('contracts.view');
  const q = useQuery(documentQuery(docId));
  const d = q.data;
  useCrumbs(
    d
      ? [
          { label: 'Документы', to: '/warehouse?tab=documents' },
          { label: d.number, title: DOC_KINDS[d.kind].title },
        ]
      : null,
    q.error,
  );
  const [editing, setEditing] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [reason, setReason] = useState('');
  const refresh = (doc: DocumentDetail) => {
    qc.setQueryData(documentQuery(docId).queryKey, doc);
    qc.invalidateQueries({ queryKey: ['wh'] });
  };
  const post = useMutation({
    mutationFn: () => api<DocumentDetail>(`/warehouse/documents/${docId}/post`, { method: 'POST' }),
    onSuccess: (doc) => {
      refresh(doc);
      toast(
        doc.status === 'draft' && doc.limit?.status === 'pending'
          ? 'Выдача сверх лимита отправлена руководителю проекта на согласие'
          : `${doc.number} проведён — остатки обновлены`,
      );
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const del = useMutation({
    mutationFn: () => api(`/warehouse/documents/${docId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wh'] });
      toast('Черновик удалён');
      navigate({ to: '/warehouse', search: { tab: 'documents' } });
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const reverse = useMutation({
    mutationFn: () =>
      api<DocumentDetail>(`/warehouse/documents/${docId}/reverse`, { body: { reason: reason.trim() } }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['wh'] });
      toast(`Сторно ${doc.number} проведено`);
      setReversing(false);
      navigate({ to: `/warehouse/documents/${doc.id}` });
    },
  });
  const limitDecide = useMutation({
    mutationFn: (v: { approve: boolean; comment?: string }) =>
      api<DocumentDetail>(`/warehouse/documents/${docId}/limit-decision`, { body: v }),
    onSuccess: (doc, v) => {
      refresh(doc);
      setRejecting(false);
      toast(
        v.approve
          ? 'Выдача сверх лимита согласована — кладовщик проведёт документ'
          : 'Отказано в выдаче сверх лимита',
      );
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const decide = useMutation({
    mutationFn: (v: { approve: boolean; comment?: string }) =>
      api<DocumentDetail>(`/warehouse/documents/${docId}/decide`, { body: v }),
    onSuccess: (doc, v) => {
      refresh(doc);
      setRejecting(false);
      toast(
        v.approve
          ? 'Расход утверждён — списано с рук получателя'
          : 'Отказано, материалы остаются за получателем',
      );
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const qcDecide = useMutation({
    mutationFn: (v: QcPayload & { lineId: string }) =>
      api<DocumentDetail>(`/warehouse/documents/${docId}/lines/${v.lineId}/qc`, {
        body: {
          decision: v.decision,
          note: v.note,
          sampleQty: v.sampleQty,
          sampleNote: v.sampleNote,
          protocolNo: v.protocolNo,
          protocolDate: v.protocolDate,
        },
      }),
    onSuccess: (doc, v) => {
      refresh(doc);
      toast(v.decision === 'accepted' ? 'Принято ОТК — позиция доступна к выдаче' : 'Брак оформлен');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });

  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError || !d)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const hasPrice = d.lineList.some((l) => l.price !== null);
  const isTransfer = d.kind === 'transfer';
  return (
    <Page>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {d.number}
            <Badge tone={DOC_TONE[d.status]}>{DOC_STATUSES[d.status]}</Badge>
          </span>
        }
        subtitle={`${DOC_KINDS[d.kind].title} от ${date(d.docDate)} · ${d.warehouseName}${d.toWarehouseName ? ` → ${d.toWarehouseName}` : ''}`}
        actions={
          <>
            {d.can.post ? (
              <Button variant="primary" loading={post.isPending} onClick={() => post.mutate()}>
                Провести
              </Button>
            ) : null}
            {d.can.accept ? (
              <Button variant="primary" onClick={() => setAccepting(true)}>
                Принять перемещение
              </Button>
            ) : null}
            {d.can.confirmReceipt ? (
              <Button variant="primary" onClick={() => setAccepting(true)}>
                Подтвердить получение
              </Button>
            ) : null}
            {d.can.decide ? (
              <>
                <Button
                  variant="primary"
                  loading={decide.isPending && decide.variables?.approve === true}
                  onClick={() => decide.mutate({ approve: true })}
                >
                  {d.kind === 'contractor_report' ? 'Утвердить отчёт' : 'Утвердить расход'}
                </Button>
                <Button variant="danger" onClick={() => setRejecting(true)}>
                  Отказать
                </Button>
              </>
            ) : null}
            {d.can.decideLimit ? (
              <>
                <Button
                  variant="primary"
                  loading={limitDecide.isPending && limitDecide.variables?.approve === true}
                  onClick={() => limitDecide.mutate({ approve: true })}
                >
                  Согласовать сверх лимита
                </Button>
                <Button variant="danger" onClick={() => setRejecting(true)}>
                  Отказать
                </Button>
              </>
            ) : null}
            {d.can.edit ? <Button onClick={() => setEditing(true)}>Править черновик</Button> : null}
            {d.can.edit ? (
              <Button
                variant="danger"
                loading={del.isPending}
                onClick={() => {
                  if (window.confirm(`Удалить черновик ${d.number}? Это действие не отменить.`)) del.mutate();
                }}
              >
                Удалить черновик
              </Button>
            ) : null}
            {d.can.reverse ? (
              <Button variant="danger" onClick={() => setReversing(true)}>
                Сторно
              </Button>
            ) : null}
            {d.status === 'posted' || d.status === 'accepted' ? (
              <a
                href={`/api/v1/warehouse/documents/${d.id}/print`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-11 items-center gap-2 rounded-md border border-line-strong bg-surface px-4 text-[14px] font-medium text-ink hover:underline"
              >
                <Printer className="size-4" aria-hidden />
                Печатная форма
              </a>
            ) : null}
            {d.kind === 'receipt' && d.status !== 'draft' ? (
              <Button
                icon={<QrCode className="size-4" />}
                onClick={() =>
                  navigate({
                    to: '/warehouse/labels',
                    search: { items: [...new Set(d.lineList.map((l) => l.itemId))].join(','), doc: d.number },
                  })
                }
              >
                Этикетки
              </Button>
            ) : null}
            <HistoryButton table="stock_documents" rowId={d.id} title={d.number} />
          </>
        }
      />
      {d.limit && (d.limit.overs.length || d.limit.status) ? (
        <div
          className={`mb-4 rounded-md border p-3 text-[14px] ${d.limit.status === 'rejected' ? 'border-danger text-danger' : 'border-line text-ink'}`}
        >
          <div className="font-medium">
            {d.limit.status === 'approved'
              ? 'Выдача сверх лимита согласована'
              : d.limit.status === 'rejected'
                ? 'В выдаче сверх лимита отказано'
                : d.limit.status === 'pending'
                  ? 'Выдача сверх лимита ждёт решения руководителя проекта'
                  : 'Выдача превышает лимит по работе: нужна причина и согласие руководителя проекта'}
          </div>
          {d.limit.overs.map((o) => (
            <div key={o.itemName} className="text-ink-2">
              {o.itemName}: лимит {qty(o.limit)} {o.unit}, выдано ранее {qty(o.issued)}, в документе{' '}
              {qty(o.requested)}, сверх лимита {qty(o.over)}
            </div>
          ))}
          {d.limit.reason ? <div className="text-ink-2">Причина: {d.limit.reason}</div> : null}
          {d.limit.decidedByName ? (
            <div className="text-ink-2">
              Решил: {d.limit.decidedByName}
              {d.limit.decidedAt ? `, ${dateTime(d.limit.decidedAt)}` : ''}
              {d.limit.note ? ` — ${d.limit.note}` : ''}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,1fr)]">
        <Panel title="Позиции" meta={`${d.lineList.length}`} bodyClassName="p-0">
          <ul className="divide-y divide-line md:hidden">
            {d.lineList.map((l) => (
              <li key={l.id} className="flex flex-col gap-1 px-4 py-3 text-[13px]">
                <Link
                  to={`/warehouse/items/${l.itemId}`}
                  className="inline-flex min-h-11 items-center text-[14px] font-medium text-ink hover:underline"
                >
                  {l.itemName}
                </Link>
                <span className="num flex flex-wrap justify-between gap-x-3 text-ink-2">
                  <span>
                    {qty(l.quantity)} {l.unit}
                    {isTransfer && l.acceptedQuantity !== null ? ` · принято ${qty(l.acceptedQuantity)}` : ''}
                    {l.normQuantity !== null ? ` · норма ${qty(l.normQuantity)}` : ''}
                    {l.normQuantity !== null && l.quantity > l.normQuantity ? (
                      <span className="text-danger"> · перерасход {qty(l.quantity - l.normQuantity)}</span>
                    ) : null}
                    {l.price !== null ? ` × ${tenge(l.price)}` : ''}
                  </span>
                  {l.price !== null ? <span className="text-ink">{tenge(l.price * l.quantity)}</span> : null}
                </span>
                {l.batch || l.certNumber || l.passportNumber || l.expiresOn ? (
                  <span className="text-ink-2">
                    {[
                      l.batch && `партия ${l.batch}`,
                      l.certNumber && `сертификат ${l.certNumber}`,
                      l.passportNumber && `паспорт ${l.passportNumber}`,
                      l.expiresOn && `годен до ${date(l.expiresOn)}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                ) : null}
                {l.qcStatus !== 'none' ? (
                  <span className="mt-1 flex flex-wrap items-center gap-2">
                    <Badge tone={QC_TONE[l.qcStatus]}>{QC_STATUSES[l.qcStatus]}</Badge>
                    {l.qcNote ? <span className="text-ink-2">{l.qcNote}</span> : null}
                    {l.qcSampleQty || l.qcProtocolNo ? (
                      <span className="text-ink-2">
                        {l.qcSampleQty
                          ? `проб: ${l.qcSampleQty}${l.qcSampleNote ? ` (${l.qcSampleNote})` : ''}`
                          : ''}
                        {l.qcProtocolNo
                          ? `${l.qcSampleQty ? ' · ' : ''}протокол ${l.qcProtocolNo}${l.qcProtocolDate ? ` от ${date(l.qcProtocolDate)}` : ''}`
                          : ''}
                      </span>
                    ) : null}
                    {l.qcStatus === 'pending' && d.can.qc ? (
                      <QcButtons onDecide={(v) => qcDecide.mutate({ lineId: l.id, ...v })} />
                    ) : null}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          <ScrollX className="hidden md:block">
            <table className="w-full min-w-[720px] text-[14px]">
              <thead>
                <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                  <th className="py-2.5 pr-3 pl-5 font-medium">Позиция</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Количество</th>
                  {isTransfer ? <th className="py-2.5 pr-3 text-right font-medium">Принято</th> : null}
                  {hasPrice ? <th className="py-2.5 pr-3 text-right font-medium">Цена</th> : null}
                  {hasPrice ? <th className="py-2.5 pr-3 text-right font-medium">Сумма</th> : null}
                  <th className="py-2.5 pr-5 font-medium">Партия и документы</th>
                </tr>
              </thead>
              <tbody>
                {d.lineList.map((l) => (
                  <tr key={l.id} className="border-t border-line align-top">
                    <td className="py-2.5 pr-3 pl-5">
                      <Link
                        to={`/warehouse/items/${l.itemId}`}
                        className="font-medium text-ink hover:underline"
                      >
                        {l.itemName}
                      </Link>
                      <div className="num text-[12px] text-ink-2">{l.itemCode}</div>
                      {l.reservationId ? (
                        <div className="text-[12px] text-ink-2">закрывает резерв</div>
                      ) : null}
                    </td>
                    <td className="num py-2.5 pr-3 text-right">
                      {qty(l.quantity)} {l.unit}
                      {l.normQuantity !== null ? (
                        <div
                          className={`text-[12px] ${l.quantity > l.normQuantity ? 'text-danger' : 'text-ink-2'}`}
                        >
                          норма {qty(l.normQuantity)}
                          {l.quantity > l.normQuantity
                            ? ` · перерасход ${qty(l.quantity - l.normQuantity)}`
                            : ''}
                        </div>
                      ) : null}
                    </td>
                    {isTransfer ? (
                      <td className="num py-2.5 pr-3 text-right">
                        {l.acceptedQuantity === null ? '—' : qty(l.acceptedQuantity)}
                        {l.acceptedQuantity !== null && l.acceptedQuantity < l.quantity ? (
                          <div className="text-[12px] text-danger">
                            недостача {qty(l.quantity - l.acceptedQuantity)}
                          </div>
                        ) : null}
                      </td>
                    ) : null}
                    {hasPrice ? (
                      <td className="num py-2.5 pr-3 text-right">
                        {l.price === null ? '—' : tenge(l.price)}
                      </td>
                    ) : null}
                    {hasPrice ? (
                      <td className="num py-2.5 pr-3 text-right">
                        {l.price === null ? '—' : tenge(l.price * l.quantity)}
                      </td>
                    ) : null}
                    <td className="py-2.5 pr-5 text-[13px]">
                      {l.batch ? <div>партия {l.batch}</div> : null}
                      {l.certNumber ? <div>сертификат {l.certNumber}</div> : null}
                      {l.passportNumber ? <div>паспорт {l.passportNumber}</div> : null}
                      {l.expiresOn ? <div>годен до {date(l.expiresOn)}</div> : null}
                      {l.qcStatus !== 'none' ? (
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <Badge tone={QC_TONE[l.qcStatus]}>{QC_STATUSES[l.qcStatus]}</Badge>
                          {l.qcNote ? <span className="text-ink-2">{l.qcNote}</span> : null}
                          {l.qcSampleQty || l.qcProtocolNo ? (
                            <span className="text-ink-2">
                              {l.qcSampleQty
                                ? `проб: ${l.qcSampleQty}${l.qcSampleNote ? ` (${l.qcSampleNote})` : ''}`
                                : ''}
                              {l.qcProtocolNo
                                ? `${l.qcSampleQty ? ' · ' : ''}протокол ${l.qcProtocolNo}${l.qcProtocolDate ? ` от ${date(l.qcProtocolDate)}` : ''}`
                                : ''}
                            </span>
                          ) : null}
                          {l.qcStatus === 'pending' && d.can.qc ? (
                            <QcButtons onDecide={(v) => qcDecide.mutate({ lineId: l.id, ...v })} />
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        </Panel>
        <div className="flex flex-col gap-6">
          <Panel title="Документ">
            <KeyValue
              items={[
                { label: 'Основание', value: d.basis ?? '—' },
                ...(d.projectCode ? [{ label: 'Проект', value: d.projectCode }] : []),
                ...(d.counterpartyName ? [{ label: 'Контрагент', value: d.counterpartyName }] : []),
                ...(d.contractId && d.contractNumber
                  ? [
                      {
                        label: 'Договор',
                        value: canContracts ? (
                          <Link
                            to="/contracts/$contractId"
                            params={{ contractId: d.contractId }}
                            className="inline-flex min-h-11 items-center text-ink hover:underline"
                          >
                            {d.contractNumber}
                          </Link>
                        ) : (
                          d.contractNumber
                        ),
                      },
                    ]
                  : []),
                ...(d.recipientName
                  ? [{ label: d.kind === 'return' ? 'Возвращает' : 'Получатель', value: d.recipientName }]
                  : []),
                ...(d.activityLabel ? [{ label: 'Работа графика', value: d.activityLabel }] : []),
                ...(d.reason ? [{ label: 'Причина', value: d.reason }] : []),
                ...(d.amount !== null ? [{ label: 'Сумма', value: tenge(d.amount) }] : []),
                { label: 'Создал', value: `${d.createdByName ?? '—'}, ${dateTime(d.createdAt)}` },
                ...(d.postedAt
                  ? [{ label: 'Провёл', value: `${d.postedByName ?? '—'}, ${dateTime(d.postedAt)}` }]
                  : []),
                ...(d.acceptedAt
                  ? [{ label: 'Принял', value: `${d.acceptedByName ?? '—'}, ${dateTime(d.acceptedAt)}` }]
                  : []),
                ...(d.inventoryId
                  ? [
                      {
                        label: 'Ведомость',
                        value: (
                          <Link className="hover:underline" to={`/warehouse/inventories/${d.inventoryId}`}>
                            {d.basis ?? 'инвентаризация'}
                          </Link>
                        ),
                      },
                    ]
                  : []),
                ...(d.reversalOfNumber && d.reversalOfId
                  ? [
                      {
                        label: 'Сторно',
                        value: (
                          <Link className="hover:underline" to={`/warehouse/documents/${d.reversalOfId}`}>
                            {d.reversalOfNumber}
                          </Link>
                        ),
                      },
                    ]
                  : []),
                ...(d.reversedByNumber && d.reversedById
                  ? [
                      {
                        label: 'Сторнирован',
                        value: (
                          <Link
                            className="text-danger hover:underline"
                            to={`/warehouse/documents/${d.reversedById}`}
                          >
                            {d.reversedByNumber}
                          </Link>
                        ),
                      },
                    ]
                  : []),
                ...(d.note
                  ? [{ label: 'Примечание', value: <span className="whitespace-pre-line">{d.note}</span> }]
                  : []),
              ]}
            />
          </Panel>
          {d.status === 'draft' ? (
            <p className="text-[13px] text-ink-2">
              Черновик остаток не меняет. После проведения документ не правится: ошибка исправляется сторно с
              причиной (СК-04).
            </p>
          ) : null}
        </div>
      </div>
      {editing ? (
        <DocumentDialog open onOpenChange={(v) => !v && setEditing(false)} edit={d} onSaved={refresh} />
      ) : null}
      {reversing ? (
        <Dialog
          open
          onOpenChange={(v) => !v && setReversing(false)}
          title={`Сторно ${d.number}`}
          description="Движения документа будут проведены в обратную сторону; остаток не может уйти в минус"
          width="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setReversing(false)}>
                Отмена
              </Button>
              <Button
                variant="danger"
                loading={reverse.isPending}
                disabled={reason.trim().length < 3}
                onClick={() => reverse.mutate()}
              >
                Провести сторно
              </Button>
            </>
          }
        >
          <Field label="Причина">
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <ErrorBox error={reverse.error} className="mt-3" />
        </Dialog>
      ) : null}
      {accepting ? (
        <AcceptDialog
          d={d}
          receipt={d.can.confirmReceipt}
          onClose={() => setAccepting(false)}
          onDone={refresh}
        />
      ) : null}
      {rejecting ? (
        <Dialog
          open
          onOpenChange={(v) => !v && setRejecting(false)}
          title={d.can.decideLimit ? 'Отказать в выдаче сверх лимита' : 'Отказать в списании'}
          description={
            d.can.decideLimit
              ? 'Документ не будет проведён; кладовщик может уменьшить количество'
              : 'Материалы остаются за получателем; причина попадёт в примечание документа'
          }
          width="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setRejecting(false)}>
                Отмена
              </Button>
              <Button
                variant="danger"
                loading={decide.isPending || limitDecide.isPending}
                disabled={rejectReason.trim().length < 3}
                onClick={() =>
                  (d.can.decideLimit ? limitDecide : decide).mutate({
                    approve: false,
                    comment: rejectReason.trim(),
                  })
                }
              >
                Отказать
              </Button>
            </>
          }
        >
          <Field label="Причина">
            <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
          </Field>
        </Dialog>
      ) : null}
    </Page>
  );
}

type QcPayload = {
  decision: 'accepted' | 'rejected';
  note?: string;
  sampleQty?: number;
  sampleNote?: string;
  protocolNo?: string;
  protocolDate?: string;
};

/** Решение ОТК по партии (КЧ-02): пробы и протокол испытаний — необязательно, брак — с причиной */
function QcButtons({ onDecide }: { onDecide: (v: QcPayload) => void }) {
  const [open, setOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const [sample, setSample] = useState('');
  const [sampleNote, setSampleNote] = useState('');
  const [protocolNo, setProtocolNo] = useState('');
  const [protocolDate, setProtocolDate] = useState('');
  const extra = (): Omit<QcPayload, 'decision' | 'note'> => ({
    ...(parseNum(sample) ? { sampleQty: parseNum(sample)! } : {}),
    ...(sampleNote.trim() ? { sampleNote: sampleNote.trim() } : {}),
    ...(protocolNo.trim() ? { protocolNo: protocolNo.trim() } : {}),
    ...(protocolNo.trim() && protocolDate ? { protocolDate } : {}),
  });
  return (
    <span className="flex flex-wrap items-center gap-2">
      {open ? (
        <span className="flex w-full flex-wrap items-center gap-2">
          <Input
            className="h-11 w-28"
            inputMode="decimal"
            placeholder="Проб, шт."
            aria-label="Отобрано проб"
            value={sample}
            onChange={(e) => setSample(e.target.value)}
          />
          <Input
            className="h-11 w-44"
            placeholder="Куда отправлены пробы"
            aria-label="Примечание к пробам"
            value={sampleNote}
            onChange={(e) => setSampleNote(e.target.value)}
          />
          <Input
            className="h-11 w-36"
            placeholder="№ протокола"
            aria-label="Номер протокола"
            value={protocolNo}
            onChange={(e) => setProtocolNo(e.target.value)}
          />
          <Input
            className="h-11 w-40"
            type="date"
            aria-label="Дата протокола"
            value={protocolDate}
            onChange={(e) => setProtocolDate(e.target.value)}
          />
        </span>
      ) : (
        <Button size="sm" variant="ghost" className="min-h-11" onClick={() => setOpen(true)}>
          Пробы и протокол
        </Button>
      )}
      <Button
        size="sm"
        variant="primary"
        className="min-h-11"
        onClick={() => onDecide({ decision: 'accepted', ...extra() })}
      >
        Принять
      </Button>
      {rejecting ? (
        <>
          <Input
            className="h-11 w-48"
            placeholder="Причина брака"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button
            size="sm"
            variant="danger"
            className="min-h-11"
            disabled={!note.trim()}
            onClick={() => onDecide({ decision: 'rejected', note: note.trim(), ...extra() })}
          >
            Брак
          </Button>
        </>
      ) : (
        <Button size="sm" variant="ghost" className="min-h-11" onClick={() => setRejecting(true)}>
          Брак
        </Button>
      )}
    </span>
  );
}

/** Приёмка перемещения получателем-складом (СК-07) или подтверждение получения выдачи сотрудником (СК-21) */
function AcceptDialog({
  d,
  receipt,
  onClose,
  onDone,
}: {
  d: DocumentDetail;
  receipt?: boolean;
  onClose: () => void;
  onDone: (doc: DocumentDetail) => void;
}) {
  const [vals, setVals] = useState<Record<string, string>>(() =>
    Object.fromEntries(d.lineList.map((l) => [l.id, String(l.quantity)])),
  );
  const [note, setNote] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api<DocumentDetail>(`/warehouse/documents/${d.id}/${receipt ? 'confirm-receipt' : 'accept'}`, {
        body: {
          lines: d.lineList.map((l) => ({
            lineId: l.id,
            acceptedQuantity: Number((vals[l.id] ?? '0').replace(',', '.')) || 0,
          })),
          note: note.trim() || null,
        },
      }),
    onSuccess: (doc) => {
      onDone(doc);
      toast(receipt ? 'Получение подтверждено — материалы числятся за вами' : 'Перемещение принято');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={receipt ? `Получение по ${d.number}` : `Приёмка ${d.number}`}
      description={
        receipt
          ? 'Укажите фактически полученное — только после этого материалы числятся за вами; недополученное вернётся на склад (СК-21)'
          : 'Укажите фактически принятое; недостача останется на складе «В пути», решение за отправителем (СК-07)'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>
            {receipt ? 'Подтвердить получение' : 'Подтвердить приёмку'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {d.lineList.map((l) => (
          <Field
            key={l.id}
            label={`${l.itemName} — ${receipt ? 'выдано' : 'отправлено'} ${qty(l.quantity)} ${l.unit}`}
          >
            <Input
              inputMode="decimal"
              value={vals[l.id] ?? ''}
              onChange={(e) => setVals({ ...vals, [l.id]: e.target.value })}
            />
          </Field>
        ))}
        <Field label="Замечание">
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}
